import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Lock, FileText, Users, Shield, DollarSign } from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";

const phases = [
  {
    phase: "I",
    title: "Intake & Qualification",
    icon: Lock,
    description: "Control who enters the system, when, and why—without founder involvement.",
    details: [
      "Structured application process",
      "Qualification logic & decision rules",
      "Objective intake criteria",
      "Approved, Hold, or Declined outcomes",
    ],
  },
  {
    phase: "II",
    title: "Codification",
    icon: FileText,
    description: "We extract authority from intuition and convert it into repeatable, documented systems.",
    details: [
      "Authority audit and assessment",
      "Process documentation",
      "Decision logic extraction",
      "System architecture design",
    ],
  },
  {
    phase: "III",
    title: "Certification",
    icon: Users,
    description: "Certified operators deploy authority under license, not improvisation.",
    details: [
      "Operator certification program",
      "System training protocols",
      "Deployment standards",
      "Quality benchmarks",
    ],
  },
  {
    phase: "IV",
    title: "Governance",
    icon: Shield,
    description: "Review dashboards, approval triggers, and compliance enforce consistency.",
    details: [
      "Internal review systems",
      "Approval workflows",
      "Compliance monitoring",
      "Quality enforcement",
    ],
  },
  {
    phase: "V",
    title: "Monetization",
    icon: DollarSign,
    description: "Scale revenue independent of founder involvement through licensing and renewals.",
    details: [
      "Certification fees (upfront)",
      "Annual licensing revenue",
      "Renewal & compliance fees",
      "Operator-driven growth model",
    ],
  },
];

const MethodologyPage = () => {
  return (
    <div className="min-h-screen bg-[#080808]" data-testid="methodology-page">
      <SEOHead 
        title="Methodology - How Authority Is Built"
        description="The five-phase methodology for building authority infrastructure that scales without founder dependency."
        url="https://onpointauthority.com/methodology"
      />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 px-6 md:px-12" data-testid="methodology-hero">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-6">
              Process
            </p>
            <h1 className="font-serif text-5xl md:text-7xl font-light tracking-tight leading-[0.95] text-foreground mb-8">
              How Authority<br />
              <span className="text-primary">Is Built</span>
            </h1>
            <p className="text-lg md:text-xl font-light leading-relaxed text-muted-foreground max-w-2xl">
              A five-phase methodology for transforming founder-dependent authority 
              into scalable, governable, and monetizable infrastructure.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Phases */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="phases-section">
        <div className="max-w-7xl mx-auto">
          <div className="space-y-16 md:space-y-24">
            {phases.map((phase, index) => (
              <motion.div
                key={phase.phase}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start"
                data-testid={`phase-${index}`}
              >
                {/* Phase Info */}
                <div className={index % 2 === 1 ? "lg:order-2" : ""}>
                  <div className="flex items-start gap-6 mb-6">
                    <div className="w-16 h-16 bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <span className="font-serif text-2xl text-primary">{phase.phase}</span>
                    </div>
                    <div>
                      <p className="text-xs font-mono text-muted-foreground mb-2">
                        PHASE {phase.phase}
                      </p>
                      <h2 className="font-serif text-3xl md:text-4xl font-normal text-foreground">
                        {phase.title}
                      </h2>
                    </div>
                  </div>
                  <p className="text-lg font-light text-muted-foreground leading-relaxed">
                    {phase.description}
                  </p>
                </div>

                {/* Phase Details */}
                <div className={`bg-card/5 border border-white/5 p-8 ${index % 2 === 1 ? "lg:order-1" : ""}`}>
                  <phase.icon className="h-8 w-8 text-primary mb-6" strokeWidth={1.5} />
                  <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
                    What Happens
                  </p>
                  <ul className="space-y-3">
                    {phase.details.map((detail, i) => (
                      <li key={i} className="flex items-center gap-3 text-foreground">
                        <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Outcome */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#050505]" data-testid="outcome-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Result
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-foreground mb-6">
              The<br />
              <span className="text-primary">Outcome</span>
            </h2>
            <p className="text-lg font-light leading-relaxed text-muted-foreground mb-10">
              A business that scales without dilution and survives diligence without explanation.
            </p>
            <Link to="/authority-review">
              <Button
                className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.15em] text-xs font-bold px-10 py-6 rounded-none gold-glow"
                data-testid="methodology-cta"
              >
                Request Authority Review
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MethodologyPage;
